import rawConfig from "./warehouse.json";

/**
 * Ombor geometriyasi va yurish to'ri — serverdagi `wmskit/whmap`
 * paketining AYNAN nusxasi.
 *
 * Nega ikki joyda: xarita marshrutni brauzerda darhol chizadi
 * (xodim tanlanganda so'rov kutilmaydi, namuna rejimida ham
 * ishlaydi), server esa terish tartibini o'sha yo'lak va o'tishlar
 * bo'yicha beradi. Ikkalasi BITTA sozlamadan (`warehouse.json`)
 * o'qiydi va Go testi (`TestConfigCopyInAdminMatches`) fayllar bir
 * xilligini tekshiradi. Qoidani o'zgartirsangiz — `layout.go` /
 * `grid.go` bilan birga o'zgartiring.
 *
 * Hammasi millimetrda. X — zal bo'ylab sharqqa (stellaj raqami
 * o'sadi), Z — ko'ndalangiga janubga (Nazorat tomon), Y — yuqoriga.
 */

export interface Point {
  x: number;
  z: number;
}

export interface Rect {
  x: number;
  z: number;
  w: number;
  d: number;
}

interface Band {
  kind: "gap" | "aisle" | "row";
  size?: number;
  row?: string;
  face?: "N" | "S";
}

export interface ZoneConfig {
  id: string;
  label: string;
  tone: string;
  prefixes: string[];
  rect: Rect;
  slot: { w: number; d: number; gap: number };
  walkable: boolean;
  assumed?: boolean;
}

export interface DoorConfig {
  id: string;
  label: string;
  from: Point;
  to: Point;
}

export interface WarehouseConfig {
  version: number;
  units: string;
  warehouse: string;
  grid: number;
  hall: Rect;
  /** Marshrut to'ri qamraydigan butun hudud (binolar va hovli). */
  site?: Rect;
  rack: {
    bayNarrow: number;
    bayWide: number;
    upright: number;
    depth: number;
    flue: number;
    height: number;
    wideLevels: number[];
    wideMinCells: number;
    passLevels: number[];
    passClearance: number;
    accessOffset: number;
    cellOrder: string;
    rowStartX: number;
    levelBottoms: Record<string, number>;
    levelHeights: Record<string, number>;
  };
  crossSection: Band[];
  zones: ZoneConfig[];
  walls: Array<{ from: Point; to: Point }>;
  /** Devordagi ochiq joylar (rejadagi uzilishlar). */
  doors: DoorConfig[];
  depot: { x: number; z: number; label: string };
}

export const WAREHOUSE: WarehouseConfig = rawConfig as WarehouseConfig;

export interface Bay {
  number: number;
  x0: number;
  x1: number;
  width: number;
  wide: boolean;
  /** Faqat 5–6 qavatli o'tish stellaji: ostidan yuriladi. */
  pass: boolean;
  /** qavat → yacheyka soni */
  cells: Map<number, number>;
}

export interface Row {
  code: string;
  z0: number;
  z1: number;
  face: "N" | "S";
  aisle: number;
  bays: Bay[];
  backGap: number;
}

export interface Aisle {
  index: number;
  z0: number;
  z1: number;
  rows: string[];
}

export interface Placed {
  code: string;
  kind: "RACK" | "ZONE";
  row?: string;
  bay?: number;
  level?: number;
  cell?: number;
  zone?: string;
  /** Poldagi markaz, eni (X) va chuqurligi (Z). */
  x: number;
  z: number;
  w: number;
  d: number;
  /** Tagining balandligi va o'zi. */
  y: number;
  h: number;
  /** Xodim yoki kara to'xtaydigan nuqta. */
  access: Point;
  aisle: number;
  pass: boolean;
  walkable: boolean;
}

export interface RouteResult {
  points: Point[];
  /** Yurish masofasi, mm. */
  length: number;
}

const RE_RACK = /^([A-Z])(\d{2})-(\d{2})-(\d{2})-(\d{2})$/;
const RE_ZONE_DASH = /^([A-Z]{2,4})-(\d{1,4})$/;
const RE_ZONE_SHORT = /^([A-Z])(\d{3})$/;

export function parseRackCode(code: string) {
  const match = RE_RACK.exec(code);
  if (!match) return null;
  return {
    row: `${match[1]}${match[2]}`,
    bay: Number(match[3]),
    level: Number(match[4]),
    cell: Number(match[5]),
  };
}

export function parseZoneCode(code: string): { prefix: string; number: number } | null {
  const dash = RE_ZONE_DASH.exec(code);
  if (dash) return { prefix: dash[1]!, number: Number(dash[2]) };
  const short = RE_ZONE_SHORT.exec(code);
  if (short) return { prefix: short[1]!, number: Number(short[2]) };
  return null;
}

export class Layout {
  readonly config: WarehouseConfig;
  readonly rows: Row[] = [];
  readonly aisles: Aisle[] = [];
  readonly unplaced: string[] = [];
  private readonly rowByCode = new Map<string, Row>();
  private readonly placed = new Map<string, Placed>();
  private readonly zoneByKey = new Map<string, ZoneConfig>();
  private readonly grid: Grid;

  constructor(codes: string[], config: WarehouseConfig = WAREHOUSE) {
    this.config = config;
    for (const zone of config.zones) {
      for (const prefix of zone.prefixes) this.zoneByKey.set(prefix, zone);
    }
    const rack = config.rack;

    // --- Ko'ndalang kesim ---
    let z = config.hall.z;
    const bands = config.crossSection.map((band) => {
      const size = band.kind === "row" ? rack.depth : band.size ?? 0;
      const item = { band, z0: z, z1: z + size };
      z += size;
      return item;
    });
    const aisleOf = new Map<number, number>();
    bands.forEach((item, index) => {
      if (item.band.kind === "aisle") {
        aisleOf.set(index, this.aisles.length);
        this.aisles.push({ index: this.aisles.length, z0: item.z0, z1: item.z1, rows: [] });
      }
    });
    bands.forEach((item, index) => {
      if (item.band.kind !== "row") return;
      const face = item.band.face ?? "N";
      const row: Row = { code: item.band.row!, z0: item.z0, z1: item.z1, face, aisle: -1, bays: [], backGap: 0 };
      const front = face === "N" ? index - 1 : index + 1;
      const back = face === "N" ? index + 1 : index - 1;
      if (bands[front]?.band.kind === "aisle") {
        row.aisle = aisleOf.get(front)!;
        this.aisles[row.aisle]!.rows.push(row.code);
      }
      const backBand = bands[back]?.band;
      if (backBand?.kind === "gap" && (backBand.size ?? 0) <= 600) row.backGap = backBand.size ?? 0;
      this.rows.push(row);
      this.rowByCode.set(row.code, row);
    });

    // --- Kodlar: qator → stellaj → qavat → yacheyka soni ---
    const rackCells = new Map<string, Map<number, Map<number, number>>>();
    const zoneCodes: string[] = [];
    for (const code of codes) {
      const parsed = parseRackCode(code);
      if (parsed) {
        if (!this.rowByCode.has(parsed.row)) {
          this.unplaced.push(code);
          continue;
        }
        const byBay = rackCells.get(parsed.row) ?? new Map<number, Map<number, number>>();
        rackCells.set(parsed.row, byBay);
        const cells = byBay.get(parsed.bay) ?? new Map<number, number>();
        byBay.set(parsed.bay, cells);
        cells.set(parsed.level, Math.max(cells.get(parsed.level) ?? 0, parsed.cell));
        continue;
      }
      zoneCodes.push(code);
    }

    // --- Stellajlar ---
    const wideLevels = new Set(rack.wideLevels);
    const passLevels = new Set(rack.passLevels);
    for (const row of this.rows) {
      const byBay = rackCells.get(row.code);
      if (!byBay || byBay.size === 0) continue;
      const maxBay = Math.max(...byBay.keys());
      let x = config.hall.x + rack.rowStartX;
      for (let number = 1; number <= maxBay; number++) {
        const cells = byBay.get(number) ?? new Map<number, number>();
        let wide = false;
        let pass = cells.size > 0;
        for (const [level, count] of cells) {
          if (wideLevels.has(level) && count >= rack.wideMinCells) wide = true;
          if (!passLevels.has(level)) pass = false;
        }
        const width = wide ? rack.bayWide : rack.bayNarrow;
        const bay: Bay = { number, x0: x, x1: x + rack.upright + width, width, wide, pass, cells };
        x = bay.x1;
        row.bays.push(bay);
      }
    }

    // --- Stellaj yacheykalari ---
    for (const code of codes) {
      const parsed = parseRackCode(code);
      if (!parsed) continue;
      const row = this.rowByCode.get(parsed.row);
      const bay = row?.bays[parsed.bay - 1];
      if (!row || !bay) continue;
      const count = Math.max(1, bay.cells.get(parsed.level) ?? 1);
      const cellW = Math.trunc(bay.width / count);
      const index = rack.cellOrder === "east-to-west" ? count - parsed.cell + 1 : parsed.cell;
      const x = bay.x0 + rack.upright + (index - 1) * cellW + Math.trunc(cellW / 2);
      const access = { x, z: row.face === "N" ? row.z0 - rack.accessOffset : row.z1 + rack.accessOffset };
      const height = rack.levelHeights[String(parsed.level)] ?? 1000;
      this.placed.set(code, {
        code, kind: "RACK", row: row.code, bay: parsed.bay, level: parsed.level, cell: parsed.cell,
        x, z: Math.trunc((row.z0 + row.z1) / 2), w: cellW, d: rack.depth,
        y: rack.levelBottoms[String(parsed.level)] ?? 0, h: height,
        access, aisle: row.aisle, pass: bay.pass, walkable: true,
      });
    }

    // --- Hudud yacheykalari: pol bo'ylab qatorma-qator ---
    zoneCodes.sort();
    for (const code of zoneCodes) {
      const parsed = parseZoneCode(code);
      const zone = parsed ? this.zoneByKey.get(parsed.prefix) : undefined;
      if (!parsed || !zone || parsed.number < 1) {
        this.unplaced.push(code);
        continue;
      }
      const slot = zone.slot.w > 0 && zone.slot.d > 0 ? zone.slot : { w: 1200, d: 1000, gap: 300 };
      const cols = Math.max(1, Math.trunc((zone.rect.w - slot.gap) / (slot.w + slot.gap)));
      const index = parsed.number - 1;
      const col = index % cols;
      const line = Math.trunc(index / cols);
      const x = zone.rect.x + slot.gap + col * (slot.w + slot.gap) + Math.trunc(slot.w / 2);
      const zc = zone.rect.z + slot.gap + line * (slot.d + slot.gap) + Math.trunc(slot.d / 2);
      this.placed.set(code, {
        code, kind: "ZONE", zone: zone.id, x, z: zc, w: slot.w, d: slot.d, y: 0, h: 1200,
        access: { x, z: zc }, aisle: -1, pass: false, walkable: zone.walkable,
      });
    }

    this.grid = new Grid(this);
  }

  locate(code: string): Placed | undefined {
    return this.placed.get(code);
  }

  all(): Placed[] {
    return [...this.placed.values()];
  }

  row(code: string): Row | undefined {
    const row = this.rowByCode.get(code);
    return row && row.bays.length > 0 ? row : undefined;
  }

  zoneFor(prefix: string): ZoneConfig | undefined {
    return this.zoneByKey.get(prefix);
  }

  depot(): Point {
    return { x: this.config.depot.x, z: this.config.depot.z };
  }

  /** Zal ichida (marshrut to'rida) yuriladigan nuqtami. */
  walkable(point: Point): boolean {
    return this.grid.snap(point) >= 0;
  }

  /**
   * Nuqta AYNAN ochiq katakdami (FPV to'qnashuvi). `snap` dan farqli
   * o'laroq yaqin katakka tortmaydi: stellaj yoki devor — yopiq.
   */
  isFree(point: Point): boolean {
    return this.grid.free(point);
  }

  /** Eng yaqin ochiq katak markazi (FPV boshlanish nuqtasi uchun). */
  nearestFree(point: Point): Point | null {
    if (this.grid.free(point)) return point;
    const cell = this.grid.snap(point);
    return cell < 0 ? null : this.grid.centerOf(cell);
  }

  /** Ikki nuqta orasidagi eng qisqa yo'l (A*). */
  route(from: Point, to: Point): RouteResult | null {
    const start = this.grid.snap(from);
    const goal = this.grid.snap(to);
    const found = this.grid.astar(start, goal);
    if (!found) return null;
    const points = this.grid.polyline(found.cells);
    if (points.length > 0) {
      points[0] = from;
      points[points.length - 1] = to;
    }
    return { points, length: found.length };
  }

  /** Bir nechta to'xtash bo'ylab yo'l (kelgan tartibda). */
  routeThrough(stops: Point[]): RouteResult | null {
    if (stops.length < 2) return null;
    const points: Point[] = [];
    let length = 0;
    for (let i = 1; i < stops.length; i++) {
      const leg = this.route(stops[i - 1]!, stops[i]!);
      if (!leg) continue;
      length += leg.length;
      points.push(...(points.length > 0 ? leg.points.slice(1) : leg.points));
    }
    return points.length > 1 ? { points, length } : null;
  }
}

/** Kodlardan joylashuv quradi (`whmap.Build`). */
export function buildLayout(codes: string[], config: WarehouseConfig = WAREHOUSE): Layout {
  return new Layout(codes, config);
}

// ---------------------------------------------------------------------------
// Yurish to'ri (`grid.go` nusxasi)
// ---------------------------------------------------------------------------

const DIAG = 1.414;
const NEIGHBOURS: Array<[number, number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
];

class Grid {
  readonly size: number;
  readonly x0: number;
  readonly z0: number;
  readonly cols: number;
  readonly rows: number;
  readonly blocked: Uint8Array;

  constructor(layout: Layout) {
    const cfg = layout.config;
    const bounds = cfg.site && cfg.site.w > 0 && cfg.site.d > 0 ? cfg.site : cfg.hall;
    this.size = cfg.grid;
    this.x0 = bounds.x;
    this.z0 = bounds.z;
    this.cols = Math.ceil(bounds.w / this.size);
    this.rows = Math.ceil(bounds.d / this.size);
    this.blocked = new Uint8Array(this.cols * this.rows);

    for (const row of layout.rows) {
      let z0 = row.z0;
      let z1 = row.z1;
      if (row.face === "N") z1 += Math.trunc(row.backGap / 2);
      else z0 -= Math.trunc(row.backGap / 2);
      for (const bay of row.bays) {
        if (!bay.pass) this.blockRect(bay.x0, z0, bay.x1, z1);
      }
    }
    for (const wall of cfg.walls) this.blockSegment(wall.from, wall.to);
    // Eshik: devor chizig'idagi, markazi eshik ICHIDA bo'lgan kataklar ochiladi.
    for (const door of cfg.doors ?? []) this.openDoor(door);
  }

  private openDoor(door: DoorConfig) {
    if (door.from.z === door.to.z) {
      const [, r] = this.cellOf(door.from);
      const lo = Math.min(door.from.x, door.to.x);
      const hi = Math.max(door.from.x, door.to.x);
      for (let c = 0; c < this.cols; c++) {
        const x = this.center(c, r).x;
        if (x > lo && x < hi && this.inside(c, r)) this.blocked[this.index(c, r)] = 0;
      }
      return;
    }
    const [c] = this.cellOf(door.from);
    const lo = Math.min(door.from.z, door.to.z);
    const hi = Math.max(door.from.z, door.to.z);
    for (let r = 0; r < this.rows; r++) {
      const z = this.center(c, r).z;
      if (z > lo && z < hi && this.inside(c, r)) this.blocked[this.index(c, r)] = 0;
    }
  }

  private index(c: number, r: number) {
    return r * this.cols + c;
  }

  private inside(c: number, r: number) {
    return c >= 0 && r >= 0 && c < this.cols && r < this.rows;
  }

  center(c: number, r: number): Point {
    return { x: this.x0 + c * this.size + this.size / 2, z: this.z0 + r * this.size + this.size / 2 };
  }

  private cellOf(p: Point): [number, number] {
    return [Math.floor((p.x - this.x0) / this.size), Math.floor((p.z - this.z0) / this.size)];
  }

  private blockRect(x0: number, z0: number, x1: number, z1: number) {
    const [c0, r0] = this.cellOf({ x: x0, z: z0 });
    const [c1, r1] = this.cellOf({ x: x1, z: z1 });
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        if (!this.inside(c, r)) continue;
        const p = this.center(c, r);
        if (p.x >= x0 && p.x <= x1 && p.z >= z0 && p.z <= z1) this.blocked[this.index(c, r)] = 1;
      }
    }
  }

  private blockSegment(from: Point, to: Point) {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const steps = Math.trunc(Math.hypot(dx, dz) / (this.size / 4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const [c, r] = this.cellOf({ x: from.x + (dx * i) / steps, z: from.z + (dz * i) / steps });
      if (this.inside(c, r)) this.blocked[this.index(c, r)] = 1;
    }
  }

  free(p: Point): boolean {
    const [c, r] = this.cellOf(p);
    return this.inside(c, r) && !this.blocked[this.index(c, r)];
  }

  centerOf(cell: number): Point {
    return this.center(cell % this.cols, Math.trunc(cell / this.cols));
  }

  snap(p: Point): number {
    const [c, r] = this.cellOf(p);
    if (this.inside(c, r) && !this.blocked[this.index(c, r)]) return this.index(c, r);
    for (let radius = 1; radius <= 12; radius++) {
      let best = -1;
      let bestDist = Infinity;
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          if (Math.abs(dr) !== radius && Math.abs(dc) !== radius) continue;
          const cc = c + dc;
          const rr = r + dr;
          if (!this.inside(cc, rr) || this.blocked[this.index(cc, rr)]) continue;
          const center = this.center(cc, rr);
          const d = (center.x - p.x) ** 2 + (center.z - p.z) ** 2;
          if (d < bestDist) {
            best = this.index(cc, rr);
            bestDist = d;
          }
        }
      }
      if (best >= 0) return best;
    }
    return -1;
  }

  private heuristic(a: number, b: number) {
    const dc = Math.abs((a % this.cols) - (b % this.cols));
    const dr = Math.abs(Math.trunc(a / this.cols) - Math.trunc(b / this.cols));
    const lo = Math.min(dc, dr);
    const hi = Math.max(dc, dr);
    return (hi - lo) * this.size + lo * this.size * DIAG;
  }

  astar(start: number, goal: number): { cells: number[]; length: number } | null {
    if (start < 0 || goal < 0) return null;
    if (start === goal) return { cells: [start], length: 0 };
    const total = this.blocked.length;
    const dist = new Float64Array(total).fill(Infinity);
    const parent = new Int32Array(total).fill(-1);
    dist[start] = 0;
    const heap = new MinHeap();
    heap.push(start, this.heuristic(start, goal));
    while (heap.size > 0) {
      const [node, priority] = heap.pop();
      if (node === goal) break;
      const base = dist[node]!;
      if (priority - this.heuristic(node, goal) > base + 1e-6) continue;
      const c = node % this.cols;
      const r = Math.trunc(node / this.cols);
      for (const [dc, dr] of NEIGHBOURS) {
        const cc = c + dc;
        const rr = r + dr;
        if (!this.inside(cc, rr) || this.blocked[this.index(cc, rr)]) continue;
        let cost = this.size;
        if (dc !== 0 && dr !== 0) {
          if (this.blocked[this.index(c + dc, r)] || this.blocked[this.index(c, r + dr)]) continue;
          cost = this.size * DIAG;
        }
        const next = this.index(cc, rr);
        const nd = base + cost;
        if (nd < dist[next]!) {
          dist[next] = nd;
          parent[next] = node;
          heap.push(next, nd + this.heuristic(next, goal));
        }
      }
    }
    if (!Number.isFinite(dist[goal]!)) return null;
    const cells: number[] = [];
    for (let node = goal; node !== -1; node = parent[node]!) cells.push(node);
    cells.reverse();
    return { cells, length: Math.round(dist[goal]!) };
  }

  polyline(cells: number[]): Point[] {
    if (cells.length === 0) return [];
    const at = (cell: number) => this.center(cell % this.cols, Math.trunc(cell / this.cols));
    const points = [at(cells[0]!)];
    for (let i = 1; i < cells.length - 1; i++) {
      const prev = cells[i - 1]!;
      const cur = cells[i]!;
      const next = cells[i + 1]!;
      const d1 = [(cur % this.cols) - (prev % this.cols), Math.trunc(cur / this.cols) - Math.trunc(prev / this.cols)];
      const d2 = [(next % this.cols) - (cur % this.cols), Math.trunc(next / this.cols) - Math.trunc(cur / this.cols)];
      if (d1[0] !== d2[0] || d1[1] !== d2[1]) points.push(at(cur));
    }
    if (cells.length > 1) points.push(at(cells[cells.length - 1]!));
    return points;
  }
}

/** Ikkilik uyum: A* navbati. */
class MinHeap {
  private nodes: number[] = [];
  private prios: number[] = [];

  get size() {
    return this.nodes.length;
  }

  push(node: number, prio: number) {
    this.nodes.push(node);
    this.prios.push(prio);
    let i = this.nodes.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.prios[parent]! <= this.prios[i]!) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): [number, number] {
    const top: [number, number] = [this.nodes[0]!, this.prios[0]!];
    const lastNode = this.nodes.pop()!;
    const lastPrio = this.prios.pop()!;
    if (this.nodes.length > 0) {
      this.nodes[0] = lastNode;
      this.prios[0] = lastPrio;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < this.nodes.length && this.prios[l]! < this.prios[m]!) m = l;
        if (r < this.nodes.length && this.prios[r]! < this.prios[m]!) m = r;
        if (m === i) break;
        this.swap(i, m);
        i = m;
      }
    }
    return top;
  }

  private swap(a: number, b: number) {
    [this.nodes[a], this.nodes[b]] = [this.nodes[b]!, this.nodes[a]!];
    [this.prios[a], this.prios[b]] = [this.prios[b]!, this.prios[a]!];
  }
}

/**
 * Devorni eshiklar bo'yicha bo'laklaydi: chizishda uzilish ko'rinsin.
 * Faqat o'qqa parallel devor va eshiklar.
 */
export function wallPieces(config: WarehouseConfig = WAREHOUSE): Array<{ from: Point; to: Point }> {
  const pieces: Array<{ from: Point; to: Point }> = [];
  for (const wall of config.walls) {
    const horizontal = wall.from.z === wall.to.z;
    const fixed = horizontal ? wall.from.z : wall.from.x;
    const a = horizontal ? Math.min(wall.from.x, wall.to.x) : Math.min(wall.from.z, wall.to.z);
    const b = horizontal ? Math.max(wall.from.x, wall.to.x) : Math.max(wall.from.z, wall.to.z);
    const cuts = (config.doors ?? [])
      .filter((door) => (horizontal ? door.from.z === fixed && door.to.z === fixed : door.from.x === fixed && door.to.x === fixed))
      .map((door) => horizontal
        ? [Math.min(door.from.x, door.to.x), Math.max(door.from.x, door.to.x)] as const
        : [Math.min(door.from.z, door.to.z), Math.max(door.from.z, door.to.z)] as const)
      .filter(([lo, hi]) => hi > a && lo < b)
      .sort((p, q) => p[0] - q[0]);
    let cursor = a;
    const push = (lo: number, hi: number) => {
      if (hi - lo < 1) return;
      pieces.push(horizontal
        ? { from: { x: lo, z: fixed }, to: { x: hi, z: fixed } }
        : { from: { x: fixed, z: lo }, to: { x: fixed, z: hi } });
    };
    for (const [lo, hi] of cuts) {
      push(cursor, Math.max(cursor, lo));
      cursor = Math.max(cursor, hi);
    }
    push(cursor, b);
  }
  return pieces;
}
